import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FleetGeneralFigureService } from 'src/app/_services/fleet-general-figure.service';
import { BatteryTypeOption, toBatteryTypeFilter, toBatteryTypeSeries } from 'src/app/_shared/utils/battery-type';
import { KWhKmToMilesPipe } from 'src/app/_shared/pipes/k-wh-km-to-miles.pipe';
import { TransformXAxisPipe } from 'src/app/_shared/pipes/transform-xaxis.pipe';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';

@Component({
  selector: 'app-avg-fleet-efficiency-chart',
  templateUrl: './avg-fleet-efficiency-chart.component.html',
  styleUrls: ['./avg-fleet-efficiency-chart.component.scss']
})
export class AvgFleetEfficiencyChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() fleetId: string[] = [];
  @Input() selectedBatteryTypes: BatteryTypeOption[] = [];
  @Input() companyData: any;
  @Input() isMetric: boolean | undefined;
  sohcFleetDatapoints: any = [];
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  chartRange!: { range: [number, number]; };
  private readonly kWhKmToMilesPipe = new KWhKmToMilesPipe();
  
  constructor(
    private readonly fleetGeneralFigureService: FleetGeneralFigureService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  
  ngOnChanges(): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.fleetGeneralFigureService.getEnergyEfficiencyFleet(this.fleetId, toBatteryTypeFilter(this.selectedBatteryTypes)).pipe(takeUntil(this.unsubscribe$)).subscribe(
      {
        next: (datapoints) => {
          this.plotAverageFleetEfficiency(datapoints);
        },
        error: (error) => {
          console.error('Error fetching Energy Efficiency Fleet data:', error);
          this.dataLoading = false;
          this.errorMessage = true;
        },
        complete: () => {
          this.dataLoading = false;
        }
      }
    )
  }
  
  plotAverageFleetEfficiency(datapoints: any) {
    const transformXAxisPipe = new TransformXAxisPipe();
    const data = toBatteryTypeSeries(datapoints, this.selectedBatteryTypes).map((series) => ({
      x: this.getConvertedValuesByUnit(series.datapoints.map((dataObject: any) => dataObject.binEnergy)),
      y: series.datapoints.map((dataObject: any) => dataObject.percVeh),
      type: 'bar',
      name: series.label,
      marker: {
        color: series.color
      }
    }));
    this.chartRange = transformXAxisPipe.transform(data.flatMap((trace) => trace.x), data.flatMap((trace) => trace.y), 5, 100);

    const layout = {
      title: 'Average Fleet Efficiency',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      barmode: 'group',
      xaxis: {
        title: `Energy efficiency during driving [${this.getEfficiencyUnit()}]`,
        range: this.chartRange?.range
      },
      yaxis: {
        title: 'Percentage of Fleet Vehicles [%]'
      },
      showlegend: true,
      legend: {
        orientation: 'h', 
        y: -0.3, 
        xanchor: 'center', 
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    };
    
    createPlotlyChart('average-fleet-efficiency-chart', data, layout);
  }

  getConvertedValuesByUnit(arr: number[]): number[] {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? arr : arr.map(val => this.kWhKmToMilesPipe.transform(val));
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? arr.map(val => this.kWhKmToMilesPipe.transform(val))
        : arr;
    }
  }

  getEfficiencyUnit(): string {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? 'kWh/100km' : 'mi/kWh';
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? 'mi/kWh'
        : 'kWh/100km';
    }
  }
  
}